"use client";

import { type AuthenticationResult, EventType, InteractionStatus } from "@azure/msal-browser";
import { MsalProvider, useMsal } from "@azure/msal-react";
import { usePathname } from "next/navigation";
import { useEffect } from "react";
import { loginRequest } from "@/lib/msal-config";
import { msalInstance, syncTokenCookie } from "@/lib/auth";

export function AuthProvider({ children }: { children: React.ReactNode }) {
	useEffect(() => {
		const callbackId = msalInstance.addEventCallback((event) => {
			if (
				event.eventType === EventType.LOGIN_SUCCESS ||
				event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
			) {
				const result = event.payload as AuthenticationResult;
				const isCustomApiToken = result?.scopes?.some((s) =>
					loginRequest.scopes.some((ls) => ls.toLowerCase() === s.toLowerCase())
				);
				if (result?.accessToken && isCustomApiToken) {
					void syncTokenCookie(result.accessToken);
				}
			}
		});

		return () => {
			if (callbackId) msalInstance.removeEventCallback(callbackId);
		};
	}, []);

	return (
		<MsalProvider instance={msalInstance}>
			<EnsureAccount />
			{children}
		</MsalProvider>
	);
}

// Com o cookie ms_access_token válido mas sem conta MSAL (ex.: navegador reaberto:
// o cache some com a chave de criptografia, o cookie dura 50 min), o app abre sem
// usuário e sem como renovar o token. Manda para a ponte (/login), que recupera a
// conta pelo portal sem tela e volta para esta página.
function EnsureAccount() {
	const { accounts, inProgress } = useMsal();
	const pathname = usePathname();

	useEffect(() => {
		if (inProgress !== InteractionStatus.None || accounts.length > 0 || pathname === "/login") return;
		const next = `${window.location.pathname}${window.location.search}`;
		window.location.replace(`/login?next=${encodeURIComponent(next)}`);
	}, [accounts, inProgress, pathname]);

	return null;
}
